import React, { useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { formatBucket } from "../../utils/analyticsLabels";
import { formatLatency, latencyBars, latencyChange } from "../../utils/analyticsLatency";
import { alignPrevious, previousNames } from "../../utils/analyticsSeries";
import BarChart from "../charts/BarChart";
import ChartFrame from "../charts/ChartFrame";
import DeltaChip from "../charts/DeltaChip";
import LineChart from "../charts/LineChart";
import ResponsiveChart from "../charts/ResponsiveChart";
import { formatCompact } from "../charts/format";
import BarsFigure from "./BarsFigure";
import KpiStrip from "./KpiStrip";
import { countText } from "./kpis";
import Segmented from "./Segmented";

// A latencia da camada tecnica (spec do RPAA-286; as decisoes D9 e D11 sao do plano da latencia do
// backend, `docs/superpowers/plans/2026-10-19-rpaa-286-latencia-fina.md`). D9: uma latencia varia em
// percentagem relativa, menos e melhor, e sem comparacao quando o anterior so esta em parte coberto, um
// dos valores e null ou um deles e "60 s ou mais". D11: a evolucao e este componente e nao o EvolutionCard, porque o EvolutionCard soma os baldes na legenda e na
// variacao, e uma soma de medianas nao quer dizer nada: a legenda daqui traz o valor de cada periodo.
// Sem dia a tracejado e sem maximo marcado: um dia a decorrer ja tem a sua mediana, e o maximo de uma
// mediana nao e um pico de procura.
// Num periodo de um dia a serie vem por hora (item 12) e desenha-se em barras, como a das contagens, mas
// sem a hora a decorrer mais tenue, pela mesma razao: a mediana de meia hora nao e mais pequena do que a de
// uma hora inteira, como uma contagem seria. A mediana de ontem le o histograma cortado (D6 do item 12), e a
// legenda da-lhe o nome do `previousNames` ("Ontem ate as 17:30").
// Um resumo de antes do item 12 traz a serie em `daily` e sem `granularity`: le-se por dia, como o D15 le o
// resto do resumo. Sem isto o `admin` que abrisse a Tecnica com esse backend (antes do deploy dele, ou
// revertido sozinho depois) deixava a aplicacao inteira em branco, porque nao ha ErrorBoundary.
const METRICS = [
  { key: "median_ms", label: "analyticsMedian" },
  { key: "p90_ms", label: "analyticsP90" },
];
const HEIGHT = 240;

const LatencyEvolution = ({ latency, series, previousComplete, cut, timeZone }) => {
  const { translate, language } = useLanguage();
  const [metric, setMetric] = useState(METRICS[0].key);
  const byHour = series.granularity === "hour";
  const { current, previous } = series.metrics[metric];
  // Ontem nas mesmas horas, como na evolucao das contagens (`alignPrevious`).
  const alignedPrevious = alignPrevious(series, previous);
  const alignedPreviousBuckets = alignPrevious(series, series.previous_buckets);
  const points = series.buckets.map((bucket, index) => ({
    key: bucket,
    label: formatBucket(bucket, series.granularity, language),
    value: current[index],
    previous: alignedPrevious[index],
  }));
  const names = previousNames({ series, cut, timeZone, translate, language });
  const show = (ms) => formatLatency(ms, language, translate);
  const table = {
    columns: [
      { key: "bucket", label: translate(byHour ? "analyticsHour" : "analyticsDay"), format: (bucket) => formatBucket(bucket, series.granularity, language) },
      // Na tabela leem-se "3,2 s"; o ficheiro leva os milissegundos em bruto, e a unidade vai no rotulo.
      { key: "value", label: translate("analyticsCurrentPeriod"), numeric: true, format: show, skipInDownload: true },
      { key: "value_ms", label: `${translate("analyticsCurrentPeriod")} (ms, 60000 = ${translate("analyticsSecondsOrMore", { value: 60 })})`, downloadOnly: true },
      // So no ficheiro, como a evolucao das contagens.
      { key: "previousBucket", label: translate(byHour ? "analyticsPreviousPeriodHour" : "analyticsPreviousPeriodDay"), downloadOnly: true },
      { key: "previous", label: names.short, numeric: true, format: show, skipInDownload: true },
      { key: "previous_ms", label: `${names.short} (ms, 60000 = ${translate("analyticsSecondsOrMore", { value: 60 })})`, downloadOnly: true },
    ],
    rows: points.map((point, index) => ({
      key: point.key,
      bucket: point.key,
      value: point.value,
      value_ms: point.value,
      previousBucket: alignedPreviousBuckets[index],
      previous: point.previous,
      previous_ms: point.previous,
    })),
  };
  const title = translate(byHour ? "analyticsLatencyEvolutionByHour" : "analyticsLatencyEvolution");
  const chosen = METRICS.find((item) => item.key === metric);
  const pair = latency[metric];
  return (
    <ChartFrame
      title={title}
      table={table}
      downloadName={`${title} ${translate(chosen.label)}`}
      empty={[...current, ...previous].every((value) => value === null)}
      actions={
        <Segmented
          label={translate("analyticsMetric")}
          options={METRICS.map((item) => ({ value: item.key, label: translate(item.label) }))}
          value={metric}
          onChange={setMetric}
        />
      }
    >
      <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs mb-2" style={{ color: "var(--color-text-secondary)" }}>
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className={byHour ? "inline-block w-2.5 h-2.5 rounded-sm" : "inline-block w-3.5 h-0.5"} style={{ backgroundColor: "var(--chart-1)" }} />
          {translate("analyticsCurrentPeriod")}: <strong style={{ color: "var(--color-text-primary)" }}>{show(pair.value)}</strong>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className={byHour ? "inline-block w-2.5 h-2.5 rounded-sm border" : "inline-block w-3.5 border-t-2 border-dashed"}
            style={{ borderColor: "var(--chart-muted)" }}
          />
          {names.legend}: <strong style={{ color: "var(--color-text-primary)" }}>{show(pair.previous)}</strong>
        </span>
        <DeltaChip change={latencyChange(pair, previousComplete, language)} />
      </p>
      <ResponsiveChart height={HEIGHT}>
        {(width) =>
          byHour ? (
            <BarChart
              data={points}
              width={width}
              height={HEIGHT}
              colorOf={() => "var(--chart-1)"}
              label={translate(chosen.label)}
              previousLabel={names.short}
              formatValue={show}
              formatTick={(ms) => `${formatCompact(ms / 1000, language)} s`}
              tickEvery={Math.max(1, Math.ceil(points.length / Math.max(1, Math.floor(width / 48))))}
            />
          ) : (
            <LineChart
              points={points}
              width={width}
              height={HEIGHT}
              label={translate(chosen.label)}
              previousLabel={names.short}
              formatValue={show}
              formatTick={(ms) => `${formatCompact(ms / 1000, language)} s`}
              tickEvery={Math.max(1, Math.ceil(points.length / Math.max(1, Math.floor(width / 64))))}
            />
          )
        }
      </ResponsiveChart>
    </ChartFrame>
  );
};

const ReplyLatency = ({ latency, previousComplete, cut, timeZone }) => {
  const { translate, language } = useLanguage();
  const series = latency.series ?? { ...latency.daily, granularity: "day" };
  const kpi = (key, label, context) => ({
    key,
    label: translate(label),
    value: formatLatency(latency[key].value, language, translate),
    change: latencyChange(latency[key], previousComplete, language),
    spark: [series.metrics[key].current, series.metrics[key].previous],
    context,
  });
  return (
    <>
      <div className="lg:col-span-2">
        <KpiStrip
          label={translate("analyticsReplyLatency")}
          kpis={[
            kpi("median_ms", "analyticsLatencyMedian", countText(latency.turns, "analyticsLatencyReplies", translate, language)),
            kpi("p90_ms", "analyticsLatencyP90", latency.p90_ms.value === null ? null : translate("analyticsLatencyP90Context")),
          ]}
        />
      </div>
      <LatencyEvolution latency={latency} series={series} previousComplete={previousComplete} cut={cut} timeZone={timeZone} />
      <BarsFigure title={translate("analyticsReplyLatencyDistribution")} bars={latencyBars(latency.histogram, translate)} height={HEIGHT} />
    </>
  );
};

export default ReplyLatency;
